"use client";

import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "motion/react";
import { grantConsent, needsConsentAnswer, refuseConsent } from "@/lib/consent";
import { trackCurrentPage } from "@/lib/analytics";
import { pagePath } from "@/lib/address";

/**
 * The cookie banner, asking before Google Analytics is loaded. Fixed to the
 * bottom over whatever is showing, with Recusar as easy as Aceitar. Gerir
 * cookies, in the footer and the phone's legal list, opens it again.
 */
// whether the banner is up, kept outside React so a link anywhere can open it
let shown = false;
const listeners = new Set<() => void>();
const setShown = (value: boolean) => {
  shown = value;
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const openBanner = () => setShown(true);

export const COOKIE_SETTINGS_LABEL = "Gerir cookies";

export function CookieSettingsLink({ className = "", children }: { className?: string; children?: ReactNode }) {
  return (
    <button type="button" className={className} onClick={openBanner}>
      {children ?? COOKIE_SETTINGS_LABEL}
    </button>
  );
}

export function CookieBanner() {
  const open = useSyncExternalStore(subscribe, () => shown, () => false);

  // a visitor who has not answered yet is asked as the app starts
  useEffect(() => {
    if (needsConsentAnswer()) {
      openBanner();
    }
  }, []);
  const setOpen = setShown;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="region"
          aria-label="Cookies"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 34 }}
          className="fixed inset-x-3 z-[90] card border border-line bg-surface p-4 desk:inset-x-auto desk:right-6 desk:bottom-6 desk:max-w-md"
          style={{ bottom: "calc(var(--tabbar-h) + var(--safe-bottom) + 12px)" }}
        >
          <p className="text-[13.5px] leading-snug text-ink-soft">
            Usamos cookies do Google Analytics para perceber como o site é usado e melhorá-lo. Só são guardados se
            aceitares, e podes mudar de ideias quando quiseres.{" "}
            <Link href={pagePath("politica-de-cookies")!} className="font-semibold text-brand underline underline-offset-2">
              Política de Cookies
            </Link>
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              className="pressable rounded-full border border-line-strong py-2.5 text-[14px] font-semibold"
              onClick={() => {
                refuseConsent();
                setOpen(false);
              }}
            >
              Recusar
            </button>
            <button
              type="button"
              className="pressable rounded-full bg-brand py-2.5 text-[14px] font-bold text-white"
              onClick={() => {
                grantConsent();
                trackCurrentPage();
                setOpen(false);
              }}
            >
              Aceitar
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
